import { create } from 'zustand'
import { royaleApi } from '../services/royaleApi'
import useAppStore from '../../../store/useAppStore'

let inventoryRequestId = 0

const useRoyaleStore = create((set, get) => ({
  // State
  activeRound: null,
  engine: null,
  isConnected: false,
  isModeDisabled: false,
  limits: null,
  bets: [],
  users: [],
  // Банды раунда (`teams` из ответа API). Ключа нет вовсе, если банд в раунде нет
  teams: [],
  isLoading: false,
  isBetting: false,
  error: null,
  inventory: [],
  pendingItemLocks: {},
  balance: 0,
  pendingBetIds: [],
  confirmedBetKeys: new Set(),
  lastValidRound: null,
  consecutiveErrors: 0,

  // Actions
  setActiveRound: (round) => set({ activeRound: round }),
  setBets: (bets) => set({ bets }),
  setUsers: (users) => set({ users }),
  setTeams: (teams) => set({ teams }),
  setInventory: (inventory) => set({ inventory }),
  setBalance: (balance) => set({ balance }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),
  resetErrors: () => set({ consecutiveErrors: 0 }),
  hasConfirmedBetKey: (betKey) => get().confirmedBetKeys.has(betKey),
  addConfirmedBetKey: (betKey) => {
    const { confirmedBetKeys } = get()
    if (confirmedBetKeys.has(betKey)) return
    const nextKeys = new Set(confirmedBetKeys)
    nextKeys.add(betKey)
    set({ confirmedBetKeys: nextKeys })
  },

  // Добавление pending bet ID
  addPendingBetId: (betId) => {
    const { pendingBetIds } = get()
    if (!pendingBetIds.includes(betId)) {
      set({ pendingBetIds: [...pendingBetIds, betId] })
    }
  },

  // Удаление pending bet ID
  removePendingBetId: (betId) => {
    const { pendingBetIds } = get()
    set({ pendingBetIds: pendingBetIds.filter((id) => id !== betId) })
  },

  /**
   * Загрузка активного раунда (polling)
   */
  loadActiveRound: async (signal) => {
    try {
      set({ isLoading: true, error: null })
      const round = await royaleApi.getActiveRound(signal)

      set((state) => state.engine === 'node' ? { isLoading: false } : {
        activeRound: round,
        engine: round?.engine || 'worker',
        isModeDisabled: false,
        users: round.users || [],
        bets: round.bets || [],
        teams: round.teams || [],
        lastValidRound: round,
        consecutiveErrors: 0,
        isLoading: false
      })

      return round
    } catch (error) {
      const { consecutiveErrors, lastValidRound } = get()
      console.error('Ошибка загрузки активного раунда Royale:', error)
      set({
        error: error.message || 'Ошибка загрузки раунда',
        consecutiveErrors: consecutiveErrors + 1,
        isLoading: false
      })

      // Возвращаем последнее валидное состояние, чтобы UI не мигал
      return lastValidRound
    }
  },

  /**
   * Загрузка инвентаря для ставки
   */
  loadBetInventory: async () => {
    const requestId = ++inventoryRequestId
    try {
      set({ isLoading: true, error: null })
      const data = await royaleApi.getBetInventory(undefined, () => get().engine === 'node')

      if (requestId !== inventoryRequestId) return null
      set((state) => {
        const locks = Object.fromEntries(Object.entries(state.pendingItemLocks).filter(([, until]) => until > Date.now()))
        return {
          inventory: (data.inventory || []).filter((item) => !locks[item.id]),
          pendingItemLocks: locks,
          balance: state.engine === 'node' ? state.balance : Number(data.balance || 0),
          isLoading: false
        }
      })

      return data
    } catch (error) {
      if (requestId !== inventoryRequestId) return null
      console.error('Ошибка загрузки инвентаря Royale:', error)
      set({
        error: error.message || 'Инвентарь недоступен',
        isLoading: false
      })
      throw error
    }
  },

  /**
   * Отправка ставки
   */
  placeBet: async ({ roundId, crystal, items }) => {
    try {
      set({ isBetting: true, error: null })
      const response = await royaleApi.placeBet({ roundId, crystal, items })

      // Оптимистичное обновление, как в Double/Wheel500: сразу вычитаем ставку
      // и убираем поставленные предметы из инвентаря. Рефетч после ставки сверит с сервером.
      set((state) => {
        const selectedItemIds = new Set(Array.isArray(items) ? items : [])
        const nextInventory = (state.inventory || []).filter((item) => !selectedItemIds.has(item.id))
        const crystalValue = Number(crystal || 0)
        const currentBalance = Number(state.balance || 0)

        return {
          isBetting: false,
          inventory: nextInventory,
          balance: Math.max(0, currentBalance - (Number.isFinite(crystalValue) ? crystalValue : 0))
        }
      })

      // Синхронизируем оптимистичное значение в appStore, чтобы навигация и форма не расходились
      const crystalValue = Number(crystal || 0)
      if (Number.isFinite(crystalValue) && crystalValue > 0) {
        useAppStore.getState().updateBalance(-crystalValue)
      }

      if (response.bet_id) {
        get().addPendingBetId(response.bet_id)
      }

      return response
    } catch (error) {
      console.error('Ошибка отправки ставки Royale:', error)
      set({
        error: error.message || 'Ошибка отправки ставки',
        isBetting: false
      })
      throw error
    }
  },

  /**
   * Загрузка истории раундов
   */
  loadHistory: async () => {
    try {
      set({ isLoading: true, error: null })
      const [all, mine] = await Promise.all([
        royaleApi.getHistoryAll(),
        useAppStore.getState().isGuest ? Promise.resolve([]) : royaleApi.getHistoryMine()
      ])

      set({ isLoading: false })
      return { all, mine }
    } catch (error) {
      console.error('Ошибка загрузки истории Royale:', error)
      set({
        error: error.message || 'Ошибка загрузки истории',
        isLoading: false
      })
      throw error
    }
  },

  /**
   * Загрузка завершенного раунда по ID
   */
  loadRoundById: async (roundId) => {
    try {
      set({ isLoading: true, error: null })
      const round = await royaleApi.getRoundById(roundId)
      set({ isLoading: false })
      return round
    } catch (error) {
      console.error('Ошибка загрузки раунда Royale по ID:', error)
      set({
        error: error.message || 'Ошибка загрузки раунда',
        isLoading: false
      })
      throw error
    }
  }
}))

export default useRoyaleStore
